import { Country } from "./country";
import { Font } from "./font";
import { DynamicScript } from "./dynamic-script";
import { TenantStorageOption } from "./tenant-storage-option";
import { EStorageProvider } from "./Upload/e-storage-provider";
import { Tenant } from "./tenant";
import { EdgeServer } from "./edge-server";
import { Channel } from "./channel";

export class RawUser {
  get tenantId(): number {
    return this.managedTenantIds[0];
  }
  constructor(
    public userId: number,
    public username: string,
    public email: string,
    public firstName: string,
    public lastName: string,
    public managedTenantIds: number[],
    public phone: string,
    public roles: {},
    public channelPermissions: { [channelId: number]: UserChannelPermission },
    public trialEndsDate?: Date,
    public hasTwoFactor?: boolean,
    public lastAuth?: Date,
  ) {}

  public get hasManageUsersPermission(): boolean {
    return !!this.roles[RoleNames.UsersManager] || this.isSuperUser;
  }

  public get isSuperUser(): boolean {
    return !!this.roles[RoleNames.SuperUser];
  }

  public get canCreateChannels(): boolean {
    return !!this.roles[RoleNames.ChannelCreator] || this.isSuperUser;
  }

  public get isLiveManager(): boolean {
    return !!this.roles[RoleNames.LiveManager] || this.isSuperUser;
  }
  public get hasBatchUploadPermission(): boolean {
    return !!this.roles[RoleNames.BatchUpload] || this.isSuperUser;
  }

  public get hasQcApprovalPermission(): boolean {
    return !!this.roles[RoleNames.QCApproval] || this.isSuperUser;
  }

  public get hasRecordingPermission(): boolean {
    return !!this.roles[RoleNames.RecordingManager] || this.isSuperUser;
  }

  public get hasDownloadMediaPermission(): boolean {
    return !!this.roles[RoleNames.DownloadMedia] || this.isSuperUser;
  }
  public get hasEditMAMPermission(): boolean {
    return !!this.roles[RoleNames.MAM_Editor] || this.isSuperUser;
  }
  public get hasEditExternalMAMPermissions(): boolean {
    return !!this.roles[RoleNames.External_MAM_Editor] || this.isSuperUser;
  }

  public get hasEditMediaTranscodingPermission(): boolean {
    return !!this.roles[RoleNames.MediaTranscoderManager] || this.isSuperUser;
  }
  public get isSmartPlannerManager(): boolean {
    return !!this.roles[RoleNames.SmartPlannerManager] || this.isSuperUser;
  }
  public get isContentControlManager(): boolean {
    return !!this.roles[RoleNames.ContentControlManager] || this.isSuperUser;
  }
  public get isBroadcastsManager(): boolean {
    return (
      (!!this.roles[RoleNames.BroadcastsManager] || this.isSuperUser) &&
      this.hasEditMAMPermission
    );
  }

  /** Read-only: Viewer, Editor, or Manager (or superuser). */
  public get hasDestinationsViewerPermission(): boolean {
    return (
      this.isSuperUser ||
      !!this.roles[RoleNames.DestinationsViewer] ||
      !!this.roles[RoleNames.DestinationsEditor] ||
      !!this.roles[RoleNames.DestinationsManager]
    );
  }

  /** Create/edit destination configs: Editor or Manager (or superuser). */
  public get hasDestinationsEditorPermission(): boolean {
    return (
      this.isSuperUser ||
      !!this.roles[RoleNames.DestinationsEditor] ||
      !!this.roles[RoleNames.DestinationsManager]
    );
  }

  /** Full destinations administration (or superuser). */
  public get hasDestinationsManagerPermission(): boolean {
    return this.isSuperUser || !!this.roles[RoleNames.DestinationsManager];
  }
  public clone() {
    const clonedChannelPermissions = {};
    for (const channelId in this.channelPermissions) {
      clonedChannelPermissions[channelId] =
        this.channelPermissions[channelId].clone();
    }
    const clonedRoles = { ...this.roles };
    return new RawUser(
      this.userId,
      this.username,
      this.email,
      this.firstName,
      this.lastName,
      [...this.managedTenantIds],
      this.phone,
      clonedRoles,
      clonedChannelPermissions,
      this.trialEndsDate,
      this.hasTwoFactor,
    );
  }

  public hasEditChannelSettingsPermission(channel: Channel): boolean {
    return (
      (this.channelPermissions[channel.id] &&
        !!this.channelPermissions[channel.id].editSettings) ||
      channel.isBlock ||
      this.isSuperUser
    );
  }

  public hasViewSchedulerPermission(channel: Channel): boolean {
    return (
      (this.channelPermissions[channel.id] &&
        !!this.channelPermissions[channel.id].view) ||
      channel.isBlock ||
      this.isSuperUser
    );
  }

  public hasEditSchedulePermission(channel: Channel): boolean {
    return this.hasEditSchedulePermissionBy(channel.id, channel.isBlock);
  }
  public hasEditSchedulePermissionBy(
    channelId: number,
    isBlock: boolean,
  ): boolean {
    return (
      (this.channelPermissions[channelId] &&
        !!this.channelPermissions[channelId].editSchedule) ||
      isBlock ||
      this.isSuperUser
    );
  }
  public hasActivateDeactivateChannelPermission(channel: Channel): boolean {
    return (
      (this.channelPermissions[channel.id] &&
        !!this.channelPermissions[channel.id].activateDeactivateChannel) ||
      channel.isBlock ||
      this.isSuperUser
    );
  }
}
export class RoleNames {
  public static UsersManager = "UsersManager";
  public static SuperUser = "SuperUser";
  public static MAM_Editor = "MAM_Editor";
  public static External_MAM_Editor = "External_MAM_Editor";
  public static DownloadMedia = "DownloadMedia";
  public static QCApproval = "QCApproval";
  public static RecordingManager = "RecordingManager";
  public static BatchUpload = "BatchUpload";
  public static ChannelCreator = "ChannelCreator";
  public static LiveManager = "LiveManager";
  public static ApiUser = "ApiUser";
  public static MediaTranscoderManager = "MediaTranscoderManager";
  public static SmartPlannerManager = "SmartPlannerManager";
  public static ContentControlManager = "ContentControlManager";
  public static BroadcastsManager = "BroadcastsManager";
  public static DestinationsManager = "DestinationsManager";
  public static DestinationsEditor = "DestinationsEditor";
  public static DestinationsViewer = "DestinationsViewer";
}

export class ChannelPermissonNames {
  public static view = "view";
  public static editSettings = "editSettings";
  public static activateDeactivateChannel = "activateDeactivateChannel";
  public static editSchedule = "editSchedule";
}
export class UserChannelPermission {
  constructor(
    public channelId: number,
    public view: boolean = false,
    public editSchedule: boolean = false,
    public editSettings: boolean = false,
    public activateDeactivateChannel: boolean = false,
  ) {}
  public clone(): UserChannelPermission {
    return new UserChannelPermission(
      this.channelId,
      this.view,
      this.editSchedule,
      this.editSettings,
      this.activateDeactivateChannel,
    );
  }
}
export class User extends RawUser {
  constructor(
    public userId: number,
    public username: string,
    public email: string,
    public firstName: string,
    public lastName: string,
    public countries: Country[],
    public managedTenantIds: number[],
    public tenantGuid: string,
    public currentTenantId: number,
    public phone: string,
    public fonts: Font[],
    public broadcastAssetIdPerTypeLimits: number[],
    public broadcastAssetsPerTypeLimits: number[],
    public dynamicScripts: DynamicScript[],
    public timezones: [{ id: number; name: string; value: string }],
    public languages: [{ code: string; name: string }],
    public roles: {},
    public channelPermission: { [channelId: number]: UserChannelPermission },
    public metadata: [{ id: number; name: string }],
    public tenantStorageOptions: TenantStorageOption[],
    public channelAvailabilityZones: {
      id: number;
      displayName: string;
      previewUrl: string;
      htmlColor: string;
      isActive: boolean;
    }[],
    public systemEdgeServers: EdgeServer[],
    public fileStorageProvider: EStorageProvider,
    public managedTenants: Tenant[],
    public liveBroadcastingCheckPeriodMs: number,
    public trialEndsDate?: Date,
    public hasTwoFactor?: boolean,
    public lastAuth?: Date,
    public previewUrlPrefix = "",
    public channelPlaylistDurationMs = 1000 * 60 * 60 * 6,
    public channelPlaylistRequestAheadMs = 1000 * 60 * 60 * 2,
  ) {
    super(
      userId,
      username,
      email,
      firstName,
      lastName,
      managedTenantIds,
      phone,
      roles,
      channelPermission,
      trialEndsDate,
      hasTwoFactor,
      lastAuth,
    );
  }

  public getFullName(): string {
    let name = this.firstName + " " + this.lastName;

    if (name.length <= 1) {
      name = this.username;
    }

    return name;
  }
}
